import React, { useState, useEffect, useCallback } from 'react';
import tournamentLiveService from '../../services/tournamentLiveService';
import tournamentService from '../../services/tournamentService';
import tournamentNotifications from '../../utils/tournamentNotifications';
import toast from 'react-hot-toast';
// WebSocket service will be added later
import LiveStatsHeader from './components/LiveStatsHeader';
import PlayerSkinsTable from './components/PlayerSkinsTable';
import AllPlayersTable from './components/AllPlayersTable';
import LiveStatsLoader from './components/LiveStatsLoader';
import api from '../../services/api';
import './TournamentLiveEvents.css';

const TournamentLiveEvents = ({ selectedTournament, userId, onRefreshAvailablePlayers, onRefreshWallet }) => {
  const [liveStats, setLiveStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [purchasedPlayers, setPurchasedPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [myPlayers, setMyPlayers] = useState([]);
  const [myPlayersData, setMyPlayersData] = useState([]); // Store my-players data with amounts

  // Handle live stats updates
  const handleStatsUpdate = useCallback((stats, error) => {
    if (error) {
      setError(error.message || 'Failed to fetch live stats');
      setLoading(false);
      return;
    }

    setLiveStats(stats);
    setError(null);
    setLoading(false);
    setIsConnected(true);
  }, []);

  // Fetch my-players data to get purchase amounts
  const fetchMyPlayersData = useCallback(async (tournamentId) => {
    try {
      console.log(`📊 Fetching my-players data for tournament: ${tournamentId}`);
      const response = await api.get(`/tournaments/${tournamentId}/my-players`);
      
      if (response.data.success) {
        const myPlayersData = response.data.data.players;
        console.log(`✅ My-players data loaded: ${myPlayersData.length} players`);
        console.log('🔍 My-players data:', myPlayersData);
        setMyPlayersData(myPlayersData);
      }
    } catch (error) {
      console.error('Error fetching my-players data:', error);
    }
  }, []);

  // Fetch leaderboard data on page load
  const fetchLeaderboardData = useCallback(async (tournamentId) => {
    try {
      console.log(`📊 Fetching leaderboard data for tournament: ${tournamentId}`);
      const response = await api.get(`/leaderboard/tournaments/${tournamentId}/leaderboard`);
      
      if (response.data.success) {
        const leaderboardData = response.data.data;
        console.log(`✅ Leaderboard data loaded: ${leaderboardData.leaderboard.length} players`);
        
        setAllPlayers(leaderboardData.leaderboard);
        
        // Filter my players using userId
        if (userId) {
          const myPlayersFromLeaderboard = leaderboardData.leaderboard.filter(player => 
            player.userId === userId
          );
          setMyPlayers(myPlayersFromLeaderboard);
          console.log('👤 My players loaded:', myPlayersFromLeaderboard);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching leaderboard data:', error);
    }
  }, [userId]);

  // Handle player removal
  const handlePlayerRemoved = useCallback((removedGolferId) => {
    console.log(`🔄 Player removed: ${removedGolferId}`);
    
    // Refresh both leaderboard and my-players data to get the latest state
    if (selectedTournament) {
      console.log('🔄 Refreshing data after player removal...');
      fetchLeaderboardData(selectedTournament.id);
      fetchMyPlayersData(selectedTournament.id);
      
      // Also refresh available players list
      if (onRefreshAvailablePlayers) {
        console.log('🔄 Refreshing available players list...');
        onRefreshAvailablePlayers(selectedTournament.id);
      }
      
      // Refresh wallet balance to show the refund
      if (onRefreshWallet) {
        console.log('🔄 Refreshing wallet balance...');
        onRefreshWallet();
      }
    }
  }, [selectedTournament, fetchLeaderboardData, fetchMyPlayersData, onRefreshAvailablePlayers, onRefreshWallet]);

  // Handle leaderboard updates from WebSocket
  const handleLeaderboardUpdate = useCallback((leaderboardData) => {
    console.log('📊 TournamentLiveEvents received leaderboard update:', leaderboardData);
    
    if (leaderboardData && leaderboardData.leaderboard && Array.isArray(leaderboardData.leaderboard)) {
      setAllPlayers(leaderboardData.leaderboard);
      
      // Filter my players from the leaderboard data using userId
      if (userId) {
        const myPlayersFromLeaderboard = leaderboardData.leaderboard.filter(player => 
          player.userId === userId
        );
        setMyPlayers(myPlayersFromLeaderboard);
        console.log('👤 My players filtered:', myPlayersFromLeaderboard);
      }
    }
  }, [userId]);

  // Handle totals update from AllPlayersTable
  const handleTotalsUpdate = useCallback((totalsArray) => {
    console.log('📊 Updating totals from AllPlayersTable:', totalsArray);
    
    // totalsArray format: [totalSkinsCompleted, totalSawoffsCompleted, totalSkinsCompletedAndCurrent, totalSawoffsCompletedAndCurrent]
    const [
      totalSkinsCompleted,
      totalSawoffsCompleted,
      totalSkinsCompletedAndCurrent,
      totalSawoffsCompletedAndCurrent
    ] = totalsArray;
    
    // Update the liveStats with new totals
    setLiveStats(prevStats => {
      if (prevStats) {
        return {
          ...prevStats,
          totalSkins: totalSkinsCompleted,
          totalSawoffs: totalSawoffsCompleted,
          totalSkinsCompletedAndCurrent: totalSkinsCompletedAndCurrent,
          totalSawoffsCompletedAndCurrent: totalSawoffsCompletedAndCurrent
        };
      }
      return prevStats;
    });
  }, []);


  // Handle skin win notifications
  const handleSkinWin = useCallback((skinWin) => {
    tournamentNotifications.showSkinWinNotification(skinWin);
  }, []);


  // Set up service listener
  useEffect(() => {
    // Create a combined callback that handles both stats updates and skin wins
    const combinedCallback = (stats, error, leaderboardData) => {
      handleStatsUpdate(stats, error);
      // Check for skin wins in the stats and handle them
      if (stats && stats.skinWins) {
        stats.skinWins.forEach(handleSkinWin);
      }
      // Handle leaderboard updates
      if (leaderboardData) {
        handleLeaderboardUpdate(leaderboardData);
      }
    };
    
    tournamentLiveService.addListener(combinedCallback);

    return () => {
      tournamentLiveService.removeListener(combinedCallback);
    };
  }, [handleStatsUpdate, handleSkinWin, handleLeaderboardUpdate]);

  // WebSocket event handlers will be added later

  // Start/stop polling when tournament changes
  useEffect(() => {
    if (selectedTournament?.id) {
      setLoading(true);
      setError(null);
      tournamentLiveService.startPolling(selectedTournament.id);
      
      // Fetch initial leaderboard data
      fetchLeaderboardData(selectedTournament.id);
      // Fetch my-players data to get purchase amounts
      fetchMyPlayersData(selectedTournament.id);
    } else {
      tournamentLiveService.stopPolling();
      setLiveStats(null);
      setPurchasedPlayers([]);
      setAllPlayers([]);
      setMyPlayers([]);
      setLoading(false);
      setError(null);
    }

    return () => {
      tournamentLiveService.stopPolling();
    };
  }, [selectedTournament?.id, fetchLeaderboardData]);


  // Handle connection status
  useEffect(() => {
    const checkConnection = () => {
      setIsConnected(tournamentLiveService.isPolling());
    };

    const interval = setInterval(checkConnection, 1000);
    return () => clearInterval(interval);
  }, []);

  if (!selectedTournament) {
    return (
      <div className="tournament-live-events">
        <div className="live-events-header">
          <h3>Tournament Live Events</h3>
        </div>
        <div className="no-tournament-selected">
          <p>Select a tournament to view live events</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-live-events">
      <div className="live-events-header">
        <h3>Tournament Live Events</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="connection-indicator">
            <div className={`connection-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
            <span className="connection-text">
              {isConnected ? 'Live' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>

      {loading && <LiveStatsLoader />}

      {error && (
        <div className="live-stats-error">
          <p>⚠️ {error}</p>
          <button 
            onClick={() => {
              setError(null);
              setLoading(true);
              tournamentLiveService.fetchLiveStats(selectedTournament.id);
            }}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      )}

      {liveStats && !loading && (
        <>
          <LiveStatsHeader 
            tournament={liveStats.tournament}
            currentRound={liveStats.currentRound}
            currentHole={liveStats.currentHole}
            totalSkins={liveStats.totalSkins}
            totalSawoffs={liveStats.totalSawoffs}
            totalSkinsCompletedAndCurrent={liveStats.totalSkinsCompletedAndCurrent}
            totalSawoffsCompletedAndCurrent={liveStats.totalSawoffsCompletedAndCurrent}
            players={allPlayers}
          />

          {myPlayers.length > 0 && (
            <AllPlayersTable 
              players={myPlayers}
              myPlayersData={myPlayersData}
              totalSkins={liveStats.totalSkinsCompletedAndCurrent || liveStats.totalSkins}
              totalSawoffs={liveStats.totalSawoffsCompletedAndCurrent || liveStats.totalSawoffs}
              onTotalsUpdate={handleTotalsUpdate}
              title="My Players"
              showTotals={false}
              tournament={liveStats.tournament}
              showRemoveButton={true}
              onPlayerRemoved={handlePlayerRemoved}
            />
          )}

          <AllPlayersTable 
            players={allPlayers}
            totalSkins={liveStats.totalSkinsCompletedAndCurrent || liveStats.totalSkins}
            totalSawoffs={liveStats.totalSawoffsCompletedAndCurrent || liveStats.totalSawoffs}
            onTotalsUpdate={handleTotalsUpdate}
            tournament={liveStats.tournament}
          />
        </>
      )}

      {!liveStats && !loading && !error && (
        <div className="waiting-for-stats">
          <p>⏳ Waiting for tournament stats...</p>
          <p className="waiting-subtitle">
            Live updates will appear here once the tournament begins
          </p>
        </div>
      )}
    </div>
  );
};

export default TournamentLiveEvents;
